import React from "react";
import Container from "@mui/material/Container";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { withTranslation } from "react-i18next";
import _ from "lodash";

import PageTitle from "../../PageTitle";
import ComplianceListItem from "./ComplianceListItem";
import ComplianceDialog from "./ComplianceDialog";

import { authContext } from "../../../contexts/AuthContext";
import {
  insertCompliance,
  listCompliances,
  deleteCompliance,
  updateCompliance,
} from "../../../services/compliances";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import i18next from "i18next";

class Compliances extends React.Component {
  constructor() {
    super();
    this.state = {
      compliances: [],
      filteredCompliances: [],
      addCompliance: {},
      addComplianceError: {},
      snackbarInfoOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "info",
    };
  }

  static contextType = authContext;

  componentDidMount() {
    this.fetchComplianceList();
  }

  fetchComplianceList = async () => {
    let complianceList = await listCompliances();
    let visibleCompliances = (complianceList || []).filter(
      (c) => c.is_global && !c.is_deleted
    );
    this.setState({
      compliances: visibleCompliances,
      filteredCompliances: visibleCompliances,
    });
  };

  handleAddComplianceOpen = () => {
    this.setState({ addCompliance: { id: -1, name_es: "", name_en: "" } });
  };

  handleAddComplianceClose = () => {
    this.setState({ addCompliance: {}, addComplianceError: {} });
  };

  handleSnackbarClose = () => {
    this.setState({ snackbarInfoOpen: false });
  };

  insertCompliance = async () => {
    const { addCompliance } = this.state;

    const params = {
      name_es: addCompliance.name_es,
      name_en: addCompliance.name_en,
      description_es: addCompliance.description_es || '',
      description_en: addCompliance.description_en || '',
      is_global: true,
      loggedUserId: this.context.auth.user.id,
    };

    try {
      await insertCompliance(params);
      await this.fetchComplianceList();
      this.setState({
        addCompliance: {},
        snackbarInfoOpen: true,
        snackbarMessage: this.props.t("saved_successfully"),
        snackbarSeverity: "success",
      });
    } catch (err) {
      console.error("Error inserting compliance:", err);
      this.setState({
        snackbarInfoOpen: true,
        snackbarMessage: this.props.t("error_saving"),
        snackbarSeverity: "error",
      });
    }
  };

  deleteComplianceHandler = async (compliance) => {
    console.log("deleting", compliance);
    try {
      await deleteCompliance(compliance.id, this.context.auth.user.id);
      await this.fetchComplianceList();
      this.setState({
        snackbarInfoOpen: true,
        snackbarMessage: this.props.t("deleted_successfully"),
        snackbarSeverity: "success",
      });
    } catch (err) {
      console.error("Error deleting compliance:", err);
      this.setState({
        snackbarInfoOpen: true,
        snackbarMessage: this.props.t("error_deleting"),
        snackbarSeverity: "error",
      });
    }
  };

  validAddCompliance = () => {
    const { addCompliance } = this.state;
    let error = {
      name_es: _.isEmpty(addCompliance.name_es),
      name_en: _.isEmpty(addCompliance.name_en),
    };
    this.setState({ addComplianceError: error });
    return !error.name_es && !error.name_en;
  };

  filter = (e) => {
    const keyword = e.target.value;
    if (keyword !== "") {
      const res = this.state.compliances.filter((c) => {
        if (i18next.language === "es") {
          return (
            (c.name_es &&
              c.name_es.toLowerCase().includes(keyword.toLowerCase())) ||
            (c.description_es &&
              c.description_es.toLowerCase().includes(keyword.toLowerCase()))
          );
        } else {
          return (
            (c.name_en &&
              c.name_en.toLowerCase().includes(keyword.toLowerCase())) ||
            (c.description_en &&
              c.description_en.toLowerCase().includes(keyword.toLowerCase()))
          );
        }
      });
      this.setState({ filteredCompliances: res });
    } else {
      this.setState({ filteredCompliances: this.state.compliances });
    }
  };

  onEdit = (compliance) => {
    this.setState({ addCompliance: { ...compliance } });
  };

  updateComplianceHandler = async () => {
    const { addCompliance } = this.state;
    const params = {
      id: addCompliance.id,
      name_es: addCompliance.name_es,
      name_en: addCompliance.name_en,
      description_es: addCompliance.description_es,
      description_en: addCompliance.description_en,
      loggedUserId: this.context.auth.user.id,
    };

    try {
      await updateCompliance(params);
      await this.fetchComplianceList();
      this.setState({
        addCompliance: {},
        snackbarInfoOpen: true,
        snackbarMessage: this.props.t("updated_successfully"),
        snackbarSeverity: "success",
      });
    } catch (err) {
      console.error("Error updating compliance:", err);
      this.setState({
        snackbarInfoOpen: true,
        snackbarMessage: this.props.t("error_updating"),
        snackbarSeverity: "error",
      });
    }
  };

  render() {
    if (this.context.auth.type === "client") {
      return <Redirect to="/" />;
    }
    const { t } = this.props;

    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container>
          <Grid item xs={6}>
            <PageTitle>{t("Compliance")}</PageTitle>
          </Grid>
          <Grid item xs={6} container justifyContent="flex-end">
            <Button
              sx={{
                m: 3,
                backgroundColor:
                  window.$brand === "PRB" ? "#17afb6" : "#102a46",
                borderRadius: window.$brand === "PRB" ? 0 : 5,
              }}
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.handleAddComplianceOpen}
            >
              {t("add_compliance")}
            </Button>
          </Grid>
        </Grid>

        <TextField
          id="compliance-search"
          label={t("Compliance_filter")}
          onChange={this.filter}
          type="text"
          fullWidth
          variant="standard"
          sx={{ mb: 4, mt: 1 }}
        />

        {this.state.filteredCompliances.length === 0 ? (
          <Typography color="text.secondary">
            {t("No compliances found")}
          </Typography>
        ) : (
          <Paper variant="outlined">
            <List disablePadding>
              {this.state.filteredCompliances.map((compliance) => (
                <ComplianceListItem
                  key={compliance.id}
                  compliance={compliance}
                  onDelete={this.deleteComplianceHandler}
                  onEdit={() => this.onEdit(compliance)}
                />
              ))}
            </List>
          </Paper>
        )}

        <ComplianceDialog
          open={!_.isEmpty(this.state.addCompliance)}
          compliance={this.state.addCompliance}
          errors={this.state.addComplianceError}
          onClose={this.handleAddComplianceClose}
          onSave={() => {
            if (this.validAddCompliance()) {
              if (this.state.addCompliance.id === -1) {
                this.insertCompliance();
              } else {
                this.updateComplianceHandler();
              }
            }
          }}
          onChange={(updated) =>
            this.setState({
              addCompliance: { ...this.state.addCompliance, ...updated },
            })
          }
        />

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={this.state.snackbarInfoOpen}
          autoHideDuration={4000}
          onClose={this.handleSnackbarClose}
          key={"bottomcenterinfo"}
        >
          <Alert
            elevation={6}
            variant="filled"
            severity={this.state.snackbarSeverity}
          >
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    );
  }
}

export default withTranslation()(Compliances);
